import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { select } from '@swf/core/store';

import useRmfFirstScreenBackButton from '../../../../hooks/useRmfFirstScreenBackButton';
import CTAButton from '../../../CTAButton/CTAButton';
import Container from '../../../Container/Container';
import useRmfFirstScreen from '../../hooks/useRmfFirstScreen';
import useTransitionScreenHandler from '../../hooks/useTransitionScreenHandler';
import RMFFeedbackButton from '../RMFFeedback/RMFFeedbackButton';
import RMFTransitionScreen from '../RMFTransitionScreen/RMFTransitionScreen';
import UniqueIdPrompt from '../RMFUniqueIdPrompt/RMFUniqueIdPrompt';

import {
  TXTCLI_WhatXs_your_nameX,
  TXTCLI_Your_name_7,
  TXTWCL_Please_provide_your_full_legal_ELL,
  TXTWCL_Please_provide_your_full_legal_ELL_2
} from './RMFLegalName.messages.json';
import Alias from './components/Alias/Alias';
import FamilyName from './components/FamilyName/FamilyName';
import GivenName from './components/GivenName/GivenName';
import LegalNameExtendedDe from './components/LegalNameExtendedDe/LegalNameExtendedDe';
import MiddleName from './components/MiddleName/MiddleName';
import SecondFamilyName from './components/SecondFamilyName/SecondFamilyName';

const RMFLegalName: React.FunctionComponent = () => {
  const site = useSelector(select.cms.getSiteId);
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);
  const isRMFPhoneVerificationStatus = useFeature(FeatureFlag.SwcRMFPhoneVerificationStatus);
  const welcomeBonus = useFeature(FeatureFlag.SwcWelcomeBonusScreen);
  const isFirstScreen = useRmfFirstScreen();
  const showTransitionIfNotFirst = site === Sites.ITA && !welcomeBonus && !isFirstScreen;
  const [isTransitionVisible, closeTransitionScreen] =
    useTransitionScreenHandler(showTransitionIfNotFirst);
  const { showBackButton, handleClick } = useRmfFirstScreenBackButton();
  const [shouldAutoFocus, setShouldAutoFocus] = useState<boolean>(false);
  const isDe = site === Sites.DE;
  const isGr = site === Sites.GR;
  const isStandAloneRmfMandatory = useSelector(select.account.isStandAloneRmfMandatory);
  const { jumpBack } = useWizardJump();
  const mobileVerifiedAccount = useSelector(select.account.isMobileVerified);

  const hasVerifiedPhone = isRMFPhoneVerificationStatus && mobileVerifiedAccount;

  useEffect(() => {
    if (isTransitionVisible !== undefined) {
      setShouldAutoFocus(isAutofocusEnabled && !isTransitionVisible);
    }
  }, [isAutofocusEnabled, isTransitionVisible]);

  const handleBackClick = () => {
    isFirstScreen
      ? jumpBack(
          isStandAloneRmfMandatory || hasVerifiedPhone ? '#rmf.rmf-phone' : '#rmf.bonus-offer'
        )
      : hasVerifiedPhone
      ? jumpBack('#rmf.rmf-phone')
      : handleClick();
  };

  return (
    <Container
      page={RMFLegalName}
      headerProps={{
        buttons: { back: isDe ? false : showBackButton, onBackClick: handleBackClick },
        title: <Translation {...TXTCLI_Your_name_7} />
      }}
      pageTopTitle={<Translation {...TXTCLI_WhatXs_your_nameX} />}
      pageTopSubtitle={
        isGr ? (
          <Translation {...TXTWCL_Please_provide_your_full_legal_ELL_2} />
        ) : (
          <Translation {...TXTWCL_Please_provide_your_full_legal_ELL} />
        )
      }
    >
      {isTransitionVisible && <RMFTransitionScreen handleButtonClick={closeTransitionScreen} />}
      <fieldset>
        <GivenName autoFocus={shouldAutoFocus} />
        <MiddleName />
        <FamilyName />
        {isDe && <LegalNameExtendedDe />}
        <Alias />
        {site === Sites.SPA && <SecondFamilyName />}
      </fieldset>
      <CTAButton />
      <UniqueIdPrompt />
      <RMFFeedbackButton />
    </Container>
  );
};

RMFLegalName.displayName = 'RMFLegalName';

export default RMFLegalName;
